import type { ReactElement } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { SurfaceCard } from '@/design/SurfaceCard';
import { formatPercent } from '@/shared/analytics/MetricFormatting';
import SignalBars from '@/shared/signals/SignalBars';
import { makeCurrencyFormatter } from '@/features/licenses/CostFormatting';
import type { LicenseCostRecord } from '@/features/licenses/LicensesApiService';

interface SpendCostBreakdownPanelProps {
    title: string;
    records: LicenseCostRecord[];
    /** The slice label a record belongs to (e.g. its model, provider, or plan). */
    keyOf: (record: LicenseCostRecord) => string;
    currency: string;
    /** `donut` where slices differentiate well (spend by model); `bars` elsewhere (long labels / few slices). */
    variant: 'donut' | 'bars';
}

interface CostSlice {
    name: string;
    value: number;
}

const MAX_SLICES = 6;
/** Slices below this share are dropped as noise (keeps rounding-to-0% lines out of the breakdown). */
const MIN_SHARE = 0.005;
const PALETTE = Array.from({ length: 7 }, (_, index) => `var(--bm-chart-${index + 1})`);

/** The cost drill rail's graphic: how the slice's spend splits across a dimension (model / provider / plan / time). Donut for well-differentiated dimensions, bars otherwise. */
export default function SpendCostBreakdownPanel({ title, records, keyOf, currency, variant }: SpendCostBreakdownPanelProps): ReactElement | null {
    const formatCurrency = makeCurrencyFormatter(currency);
    const byKey = new Map<string, number>();
    for (const record of records) {
        byKey.set(keyOf(record), (byKey.get(keyOf(record)) ?? 0) + record.amount);
    }
    const total = Array.from(byKey.values()).reduce((sum, value) => sum + value, 0);
    if (total <= 0) return null;

    const ranked = Array.from(byKey.entries())
        .filter(([, value]) => value > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([name, value]) => ({ name, value }));
    const significant = ranked.filter((slice) => slice.value / total >= MIN_SHARE);
    const visible = significant.length > 0 ? significant : ranked;
    const slices: CostSlice[] =
        visible.length > MAX_SLICES ?
            [...visible.slice(0, MAX_SLICES - 1), { name: 'Other', value: visible.slice(MAX_SLICES - 1).reduce((sum, slice) => sum + slice.value, 0) }]
        :   visible;

    return (
        <SurfaceCard $padding="md">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm" style={{ letterSpacing: '0.06em' }}>
                {title}
            </Text>
            {variant === 'bars' ?
                <SignalBars data={{ series: slices.map((slice) => ({ label: slice.name, value: slice.value })), format: 'currency' }} tokens={{ solid: 'var(--bm-color-brand-accent)' }} />
            :   <CostDonut slices={slices} total={total} formatCurrency={formatCurrency} />}
        </SurfaceCard>
    );
}

function CostDonut({ slices, total, formatCurrency }: { slices: CostSlice[]; total: number; formatCurrency: (amount: number) => string }): ReactElement {
    const top = slices[0];
    const fillFor = (index: number): string => PALETTE[index % PALETTE.length] ?? PALETTE[0]!;
    return (
        <>
            <Box style={{ position: 'relative', height: 150 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={slices} dataKey="value" nameKey="name" innerRadius={46} outerRadius={64} paddingAngle={2} stroke="none" isAnimationActive={false}>
                            {slices.map((slice, index) => (
                                <Cell key={slice.name} fill={fillFor(index)} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
                {top !== undefined && (
                    <Box style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                        <Text fw={700} ff="monospace" style={{ fontSize: '15px', lineHeight: 1 }}>
                            {formatPercent(top.value / total)}
                        </Text>
                        <Text size="xs" c="dimmed" mt={2} truncate style={{ maxWidth: 96 }}>
                            {top.name}
                        </Text>
                    </Box>
                )}
            </Box>
            <Stack gap={4} mt="sm">
                {slices.map((slice, index) => (
                    <Group key={slice.name} gap={8} wrap="nowrap" justify="space-between">
                        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                            <Box style={{ width: 8, height: 8, borderRadius: '50%', background: fillFor(index), flexShrink: 0 }} />
                            <Text size="xs" truncate>
                                {slice.name}
                            </Text>
                        </Group>
                        <Text size="xs" ff="monospace" c="dimmed" style={{ flexShrink: 0 }}>
                            {formatCurrency(slice.value)} · {formatPercent(slice.value / total)}
                        </Text>
                    </Group>
                ))}
            </Stack>
        </>
    );
}
